import { useState } from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import { apiClient } from "@/api/client";
import {
  Boxes,
  Zap,
  Shield,
  Users,
  BarChart3,
  FileText,
  FlaskConical,
  Code2,
  ArrowRight,
  Bot,
  Sparkles,
  MessageSquare,
  Rocket,
  Target,
  Layers,
  CheckCircle2,
} from "lucide-react";

// Phase 1 ease-wow curve — matches the rest of the refreshed surfaces.
const EASE_WOW: [number, number, number, number] = [0.2, 0.8, 0.2, 1];

// ---------------------------------------------------------------------------
// Hero Section
// ---------------------------------------------------------------------------

function EarlyAccessForm({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Validate password if provided
    if (password && password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post("/auth/request-access", {
        email,
        name: name || undefined,
        company: company || undefined,
        password: password || undefined,
        org_name: orgName || undefined,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "detail" in err) {
        setError((err as { detail: string }).detail);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="rounded-xl border border-green-400/20 bg-green-400/5 px-6 py-5 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-green-400" />
        <p className="mt-3 font-semibold text-green-300">Request Submitted!</p>
        <p className="mt-1 text-sm text-green-400/70">
          We&apos;ll review your request and send you an email with your login details once approved.
        </p>
      </div>
    );
  }

  const inputCls =
    "rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400 sm:flex-1";

  // Compact version: just email + button (used in CTA section)
  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
        />
        <button
          type="submit"
          disabled={submitting}
          className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-white/10 transition-all hover:bg-slate-100 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none"
        >
          {submitting ? "Sending..." : "Request Access"}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {/* Row 1: Name + Company */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
        />
        <input
          type="text"
          placeholder="Company (optional)"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className={inputCls}
        />
      </div>

      {/* Row 2: Email + Org Name */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
        />
        <input
          type="text"
          placeholder="Organization name"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          className={inputCls}
        />
      </div>

      {/* Row 3: Password (optional — but recommended) */}
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Choose a password (optional, 12+ chars)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 pr-10 text-sm text-white placeholder-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
        {password && (
          <button
            type="button"
            tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {password && password.length > 0 && password.length < 12 && (
        <p className="text-xs text-amber-400">Password must be at least 12 characters</p>
      )}
      <p className="text-xs text-slate-400">
        Set your password now to log in immediately after approval. If left blank, one will be generated for you.
      </p>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting || (password.length > 0 && password.length < 12)}
        className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-white/10 transition-all hover:bg-slate-100 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none"
      >
        {submitting ? "Sending..." : "Request Early Access"}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
      {error && (
        <div className="rounded-lg border border-red-400/20 bg-red-400/5 px-4 py-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
    </form>
  );
}

function HeroSection() {
  const reducedMotion = useReducedMotion();
  const reveal = (delay: number) =>
    reducedMotion
      ? { initial: false as const }
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.56, ease: EASE_WOW, delay },
        };
  return (
    <section className="relative overflow-hidden bg-slate-950 pt-32 pb-20 sm:pt-40 sm:pb-28 lg:pt-48 lg:pb-36">
      {/* Aurora + ambient mesh — drifts slowly for a living hero */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(6,182,212,0.08),transparent_50%)]" />
      {!reducedMotion && (
        <m.div
          aria-hidden
          className="absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500/10 blur-3xl"
          animate={{
            scale: [1, 1.08, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 10, ease: "easeInOut", repeat: Infinity }}
        />
      )}
      {reducedMotion && (
        <div className="absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500/5 blur-3xl" />
      )}

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 text-center">
        <m.div
          {...reveal(0)}
          className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-4 py-1.5 text-sm text-cyan-300 mb-8"
        >
          <Rocket className="h-3.5 w-3.5" />
          Coming Soon
        </m.div>

        <m.h1
          {...reveal(0.08)}
          className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.1]"
        >
          Your Entire Dev Team.
          <br />
          <span className="bg-gradient-to-r from-brand-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
            Powered by AI.
          </span>
        </m.h1>

        <m.p
          {...reveal(0.18)}
          className="mt-8 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed"
        >
          FTL gives you an AI-powered squad — Program Manager, Business Analyst,
          QA Engineer, Developer, and Doc Writer — that turns your ideas into
          production-ready software artifacts. Automatically.
        </m.p>

        <m.div
          {...reveal(0.28)}
          id="early-access"
          className="mt-10 mx-auto max-w-xl"
        >
          <EarlyAccessForm />
          {/* Self-serve signup is OPEN and fully provisioned (org + TRIAL
              subscription + credit balance), but this page used to offer only
              the waiting-room form — the front door had no route to /register
              at all. This is the direct path, offered ALONGSIDE early access,
              which stays exactly as it was. Unconditional (no `sm:` hiding) so
              it is reachable on a phone, where the header CTA is not. */}
          <p className="mt-4 text-sm text-slate-400">
            Don&apos;t want to wait?{" "}
            <Link
              to="/register"
              className="inline-flex items-center gap-1 font-semibold text-cyan-300 hover:text-cyan-200 transition-colors"
            >
              Create your account now
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>{" "}
            and start your free trial.
          </p>
        </m.div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AI Team Showcase
// ---------------------------------------------------------------------------

const PERSONAS = [
  {
    icon: Bot,
    name: "Felix",
    role: "Program Manager",
    personality: "The one who keeps it all together",
    description:
      "Your main point of contact. Chat with Felix about your project and he orchestrates the entire team — assigning work, tracking progress, and making sure nothing falls through the cracks.",
    gradient: "from-brand-500 to-brand-700",
  },
  {
    icon: MessageSquare,
    name: "The BA",
    role: "Business Analyst",
    personality: "Asks the questions you forgot to",
    description:
      "Runs structured Q&A sessions to turn your vision into detailed requirements, user stories, and acceptance criteria.",
    gradient: "from-violet-500 to-violet-700",
  },
  {
    icon: FlaskConical,
    name: "The QA",
    role: "Quality Engineer",
    personality: "Finds bugs before your users do",
    description:
      "Automatically generates test cases, validates coverage gaps, and tracks defects across every requirement.",
    gradient: "from-emerald-500 to-emerald-700",
  },
  {
    icon: Code2,
    name: "The Dev",
    role: "Developer",
    personality: "Architects it right the first time",
    description:
      "Breaks requirements into components, defines technical architecture, and maps implementation to your sprint plan.",
    gradient: "from-amber-500 to-amber-700",
  },
  {
    icon: FileText,
    name: "The Doc",
    role: "Doc Writer",
    personality: "Makes sure it's all written down",
    description:
      "Generates BRDs, SRS documents, user guides, and keeps documentation in sync as your project evolves.",
    gradient: "from-rose-500 to-rose-700",
  },
];

function TeamGrid() {
  const reducedMotion = useReducedMotion();
  return (
    <m.div
      initial={reducedMotion ? false : "hidden"}
      whileInView={reducedMotion ? undefined : "visible"}
      viewport={{ once: true, margin: "-60px" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.08 } },
      }}
      className="grid gap-6 sm:grid-cols-2 max-w-2xl mx-auto"
    >
      {PERSONAS.slice(1).map((persona) => (
        <m.div
          key={persona.name}
          variants={
            reducedMotion
              ? undefined
              : {
                  hidden: { opacity: 0, y: 14 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.44, ease: EASE_WOW },
                  },
                }
          }
          whileHover={
            reducedMotion ? undefined : { y: -4, transition: { duration: 0.2, ease: EASE_WOW } }
          }
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-white/10 transition-[background-color,border-color]"
        >
          <div className="flex items-start gap-4">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${persona.gradient} shadow-lg`}
            >
              <persona.icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">
                {persona.role}
              </h3>
              <p className="mt-0.5 text-xs italic text-slate-400">
                &ldquo;{persona.personality}&rdquo;
              </p>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                {persona.description}
              </p>
            </div>
          </div>
        </m.div>
      ))}
    </m.div>
  );
}

function TeamShowcase() {
  return (
    <section className="relative py-20 sm:py-28 bg-slate-950 border-t border-white/5">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Meet Your AI Team
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">
            Five specialized personas. One unified workflow.
            Each one is an expert at what they do.
          </p>
        </div>

        {/* Felix — featured card */}
        <div className="mb-8 max-w-2xl mx-auto">
          <div className="rounded-2xl border border-brand-500/20 bg-gradient-to-br from-brand-500/10 to-transparent p-8 backdrop-blur-sm">
            <div className="flex items-start gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 shadow-lg shadow-brand-500/20">
                <Bot className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-xl font-bold text-white">Felix</h3>
                  <span className="rounded-full bg-brand-500/20 px-3 py-0.5 text-xs font-medium text-brand-300">
                    Program Manager
                  </span>
                </div>
                <p className="mt-1 text-sm italic text-slate-400">
                  &ldquo;The one who keeps it all together&rdquo;
                </p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Your main point of contact. Chat with Felix about your project
                  and he&apos;ll orchestrate the entire team — assigning work,
                  tracking progress, and making sure nothing falls through the
                  cracks.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Other personas — 2x2 grid */}
        <TeamGrid />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Features Section
// ---------------------------------------------------------------------------

const FEATURES = [
  {
    icon: Target,
    title: "Requirements That Write Themselves",
    description:
      "Describe your idea in plain English. The BA persona conducts structured Q&A sessions and outputs detailed requirements, user stories, and acceptance criteria.",
  },
  {
    icon: FlaskConical,
    title: "Testing on Autopilot",
    description:
      "Test cases generated from requirements. Coverage gaps identified automatically. Defects tracked and linked back to their source. QA that never sleeps.",
  },
  {
    icon: Layers,
    title: "Sprint-Ready from Day One",
    description:
      "Break requirements into tasks, organize into sprints, and track velocity with burndown charts. Your backlog manages itself.",
  },
  {
    icon: BarChart3,
    title: "Real-Time Project Intelligence",
    description:
      "Health dashboards, risk indicators, velocity trends, and coverage metrics — all live. Know exactly where your project stands at any moment.",
  },
  {
    icon: Shield,
    title: "Full Traceability",
    description:
      "Every requirement traces to tests, tasks, and components. Change control and baselines keep your project auditable and compliant.",
  },
  {
    icon: Users,
    title: "Built for Teams",
    description:
      "Role-based access control, real-time collaboration via SSE, and multi-tenant isolation. Your team, your rules, your data.",
  },
] as const;

function FeaturesSection() {
  const reducedMotion = useReducedMotion();
  return (
    <section className="py-20 sm:py-28 bg-white" id="features">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-medium text-brand-700 mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            Capabilities
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Everything You Need to Ship
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            From the first conversation to deployment — FTL manages your entire
            software development lifecycle.
          </p>
        </div>
        <m.div
          initial={reducedMotion ? false : "hidden"}
          whileInView={reducedMotion ? undefined : "visible"}
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.06,
                delayChildren: 0.04,
              },
            },
          }}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {FEATURES.map((feature) => (
            <m.div
              key={feature.title}
              variants={
                reducedMotion
                  ? undefined
                  : {
                      hidden: { opacity: 0, y: 12 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.42, ease: EASE_WOW },
                      },
                    }
              }
              whileHover={
                reducedMotion ? undefined : { y: -4, transition: { duration: 0.2, ease: EASE_WOW } }
              }
              className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-[var(--shadow-card)] hover:border-brand-200 hover:shadow-[var(--shadow-card-hover)] transition-[border-color,box-shadow] duration-300"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 group-hover:bg-brand-100 transition-colors">
                <feature.icon className="h-5 w-5 text-slate-600 group-hover:text-brand-600 transition-colors" />
              </div>
              <h3 className="mt-5 text-base font-semibold text-slate-900">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {feature.description}
              </p>
            </m.div>
          ))}
        </m.div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How It Works
// ---------------------------------------------------------------------------

function HowItWorks() {
  const steps = [
    {
      number: "01",
      title: "Tell Felix Your Idea",
      description:
        "Open a chat and describe what you want to build. Felix asks the right questions, loops in the BA for deep-dives, and builds a shared understanding of your vision.",
      icon: MessageSquare,
    },
    {
      number: "02",
      title: "AI Builds Your Artifacts",
      description:
        "Requirements, user stories, test cases, architecture components, and documentation — generated automatically by specialized AI personas working together.",
      icon: Zap,
    },
    {
      number: "03",
      title: "Track, Iterate, Ship",
      description:
        "Manage sprints, monitor velocity, review health metrics, and use change control to evolve your project with full traceability from idea to delivery.",
      icon: Rocket,
    },
  ];

  return (
    <section className="py-20 sm:py-28 bg-slate-50" id="how-it-works">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            How It Works
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Three steps from idea to structured deliverables.
          </p>
        </div>
        <StepsGrid steps={steps} />
      </div>
    </section>
  );
}

function StepsGrid({
  steps,
}: {
  steps: { number: string; title: string; description: string; icon: typeof Rocket }[];
}) {
  const reducedMotion = useReducedMotion();
  return (
    <m.div
      initial={reducedMotion ? false : "hidden"}
      whileInView={reducedMotion ? undefined : "visible"}
      viewport={{ once: true, margin: "-60px" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.1 } },
      }}
      className="grid gap-8 sm:grid-cols-3 max-w-4xl mx-auto"
    >
      {steps.map((step, i) => (
        <m.div
          key={step.number}
          variants={
            reducedMotion
              ? undefined
              : {
                  hidden: { opacity: 0, y: 14 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.48, ease: EASE_WOW },
                  },
                }
          }
          className="relative text-center"
        >
          {/* Connector line */}
          {i < steps.length - 1 && (
            <div className="hidden sm:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-px bg-gradient-to-r from-brand-300 to-brand-100" />
          )}
          <m.div
            initial={reducedMotion ? false : { scale: 0.8, opacity: 0 }}
            whileInView={reducedMotion ? undefined : { scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={
              reducedMotion
                ? undefined
                : {
                    type: "spring",
                    stiffness: 260,
                    damping: 18,
                    delay: 0.1 + i * 0.1,
                  }
            }
            className="relative inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white border border-slate-200 shadow-[var(--shadow-card)] mb-5"
          >
            <step.icon className="h-7 w-7 text-brand-600" />
            <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-white">
              {step.number}
            </span>
          </m.div>
          <h3 className="text-lg font-semibold text-slate-900">
            {step.title}
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">
            {step.description}
          </p>
        </m.div>
      ))}
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// CTA Section (replaces Pricing)
// ---------------------------------------------------------------------------

function CTASection() {
  return (
    <section className="relative py-20 sm:py-28 bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.12),transparent_70%)]" />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
          Ready to Meet Your New Team?
        </h2>
        <p className="mt-6 text-lg text-slate-400 max-w-xl mx-auto leading-relaxed">
          FTL is launching soon. Get early access and be among the first to
          experience AI-powered software development done right.
        </p>
        <div className="mt-10 mx-auto max-w-md">
          <EarlyAccessForm compact />
        </div>
        <p className="mt-4 text-sm text-slate-400">
          We&apos;ll review your request and get back to you shortly. Or{" "}
          <Link
            to="/register"
            className="font-semibold text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            create your account now
          </Link>{" "}
          and start your free trial today.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="border-t border-white/5 bg-slate-950 py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600">
              <Boxes className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white">FTL</span>
          </div>
          {/* Five labels at 360px: the row is ~232px wide after padding and
              gaps, so without `flex-wrap` + `whitespace-nowrap` three of them
              ("How It Works", "Sign In", "Sign Up") break MID-LABEL. Wrapping
              whole items onto a second centred line reads correctly instead. */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
            <a
              href="#features"
              className="whitespace-nowrap hover:text-slate-300 transition-colors"
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="whitespace-nowrap hover:text-slate-300 transition-colors"
            >
              How It Works
            </a>
            <Link
              to="/support"
              className="whitespace-nowrap hover:text-slate-300 transition-colors"
            >
              Support
            </Link>
            <Link
              to="/login"
              className="whitespace-nowrap hover:text-slate-300 transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="whitespace-nowrap hover:text-slate-300 transition-colors"
            >
              Sign Up
            </Link>
          </div>
        </div>
        <div className="mt-8 border-t border-white/5 pt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
          <span>
            &copy; {new Date().getFullYear()} FTL Development. All rights
            reserved.
          </span>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/legal/terms" className="hover:text-slate-300">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-slate-300">Privacy</Link>
            <Link to="/legal/cookies" className="hover:text-slate-300">Cookies</Link>
            <Link to="/legal/dpa" className="hover:text-slate-300">DPA</Link>
            <Link to="/legal/subprocessors" className="hover:text-slate-300">Subprocessors</Link>
            <Link to="/legal/aup" className="hover:text-slate-300">Acceptable Use</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Landing Page
// ---------------------------------------------------------------------------

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sticky header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-slate-950/80 backdrop-blur-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600">
              <Boxes className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight">
              FTL
            </span>
            <span className="rounded-full bg-cyan-400/10 border border-cyan-400/20 px-2.5 py-0.5 text-[11px] font-medium text-cyan-400">
              Coming Soon
            </span>
          </div>
          <nav className="hidden sm:flex items-center gap-6 text-sm text-slate-400">
            <a
              href="#features"
              className="hover:text-white transition-colors"
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="hover:text-white transition-colors"
            >
              How It Works
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
            >
              Sign In
            </Link>
            {/* `hidden sm:inline` ONLY because this header row is already tight
                on a 360px phone (logo + "Coming Soon" pill + Sign In + the
                Early Access button); a third item there would overflow. Mobile
                keeps two unconditional routes to /register — the hero link and
                the footer link — so signup is never header-only. */}
            <Link
              to="/register"
              className="hidden sm:inline text-sm font-medium text-slate-400 hover:text-white transition-colors whitespace-nowrap"
            >
              Sign Up
            </Link>
            <a
              href="#early-access"
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
            >
              Get Early Access
            </a>
          </div>
        </div>
      </header>

      <HeroSection />
      <TeamShowcase />
      <FeaturesSection />
      <HowItWorks />
      <CTASection />
      <Footer />
    </div>
  );
}
